import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { getErrorMessage } from '../utils/errorHandler';
import { toast } from 'sonner';
import { Plus, Edit2, Briefcase, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import LogoLoader from '@/components/LogoLoader';

const Projects = () => {
  const { token, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pos, setPos] = useState([]);
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Project dialogs
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingPO, setEditingPO] = useState(null);
  
  const [projectForm, setProjectForm] = useState({
    client_id: '',
    name: '',
    code: '',
    start_date: '',
    end_date: '',
    lumpsum_amount: '',
    hr_lumpsum_commission: '',
    assigned_consultants: [],
    notes: ''
  });
  
  const [poForm, setPoForm] = useState({
    client_id: '',
    po_number: '',
    po_type: 'Combined',
    start_date: '',
    end_date: '',
    lumpsum_amount: '',
    assigned_consultants: [],
    consultant_rates: [],
    alert_threshold_pct: '80',
    terms: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsData, posData, clientsData, consultantsData] = await Promise.all([
        api.get('/projects', token),
        api.get('/purchase-orders', token),
        api.get('/clients', token),
        api.get('/consultants', token)
      ]);
      setProjects(projectsData);
      setPos(posData);
      setClients(clientsData);
      setConsultants(consultantsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...projectForm,
        lumpsum_amount: parseFloat(projectForm.lumpsum_amount),
        hr_lumpsum_commission: parseFloat(projectForm.hr_lumpsum_commission || 0)
      };
      
      if (editingProject) {
        await api.patch(`/projects/${editingProject.id}`, payload, token);
        toast.success('Project updated successfully');
      } else {
        await api.post('/projects', payload, token);
        toast.success('Project created successfully');
      }
      
      setShowProjectDialog(false);
      setEditingProject(null);
      loadData();
      resetProjectForm();
    } catch (error) {
      console.error('Project submit error:', error);
      toast.error(getErrorMessage(error, 'Failed to save project'));
    }
  };

  const handlePOSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...poForm,
        lumpsum_amount: parseFloat(poForm.lumpsum_amount),
        alert_threshold_pct: parseFloat(poForm.alert_threshold_pct),
        consultant_rates: poForm.consultant_rates.map(r => ({
          consultant_id: r.consultant_id,
          bill_rate: parseFloat(r.bill_rate)
        }))
      };
      
      if (editingPO) {
        await api.patch(`/purchase-orders/${editingPO.id}`, payload, token);
        toast.success('PO updated successfully');
      } else {
        await api.post('/purchase-orders', payload, token);
        toast.success('PO created successfully');
      }
      
      setShowPODialog(false);
      setEditingPO(null);
      loadData();
      resetPOForm();
    } catch (error) {
      console.error('PO submit error:', error);
      toast.error(getErrorMessage(error, 'Failed to save PO'));
    }
  };

  const toggleProjectStatus = async (projectId, currentStatus) => {
    try {
      await api.patch(`/projects/${projectId}`, { is_active: !currentStatus }, token);
      toast.success(`Project ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Toggle project status error:', error);
      toast.error(getErrorMessage(error, 'Failed to update project status'));
    }
  };

  const togglePOStatus = async (poId, currentStatus) => {
    try {
      await api.patch(`/purchase-orders/${poId}`, { is_active: !currentStatus }, token);
      toast.success(`PO ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Toggle PO status error:', error);
      toast.error(getErrorMessage(error, 'Failed to update PO status'));
    }
  };

  const resetProjectForm = () => {
    setProjectForm({
      client_id: '',
      name: '',
      code: '',
      start_date: '',
      end_date: '',
      lumpsum_amount: '',
      hr_lumpsum_commission: '',
      assigned_consultants: [],
      notes: ''
    });
  };

  const resetPOForm = () => {
    setPoForm({
      client_id: '',
      po_number: '',
      po_type: 'Combined',
      start_date: '',
      end_date: '',
      lumpsum_amount: '',
      assigned_consultants: [],
      consultant_rates: [],
      alert_threshold_pct: '80',
      terms: '',
      notes: ''
    });
  };

  const openEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      client_id: project.client_id,
      name: project.name,
      code: project.code,
      start_date: project.start_date,
      end_date: project.end_date || '',
      lumpsum_amount: project.lumpsum_amount.toString(),
      hr_lumpsum_commission: project.hr_lumpsum_commission.toString(),
      assigned_consultants: project.assigned_consultants || [],
      notes: project.notes || ''
    });
    setShowProjectDialog(true);
  };

  const openEditPO = (po) => {
    setEditingPO(po);
    setPoForm({
      client_id: po.client_id,
      po_number: po.po_number,
      po_type: po.po_type,
      start_date: po.start_date,
      end_date: po.end_date || '',
      lumpsum_amount: po.lumpsum_amount.toString(),
      assigned_consultants: po.assigned_consultants || [],
      consultant_rates: po.consultant_rates || [],
      alert_threshold_pct: po.alert_threshold_pct.toString(),
      terms: po.terms || '',
      notes: po.notes || ''
    });
    setShowPODialog(true);
  };

  const addConsultantRate = (consultantId) => {
    if (!poForm.assigned_consultants.includes(consultantId)) {
      setPoForm({
        ...poForm,
        assigned_consultants: [...poForm.assigned_consultants, consultantId],
        consultant_rates: [...poForm.consultant_rates, { consultant_id: consultantId, bill_rate: '' }]
      });
    }
  };

  const removeConsultantRate = (consultantId) => {
    setPoForm({
      ...poForm,
      assigned_consultants: poForm.assigned_consultants.filter(id => id !== consultantId),
      consultant_rates: poForm.consultant_rates.filter(r => r.consultant_id !== consultantId)
    });
  };

  const updateConsultantRate = (consultantId, rate) => {
    setPoForm({
      ...poForm,
      consultant_rates: poForm.consultant_rates.map(r =>
        r.consultant_id === consultantId ? { ...r, bill_rate: rate } : r
      )
    });
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unknown';
  const getConsultantName = (id) => consultants.find(c => c.id === id)?.name || 'Unknown';
  const isAdmin = user?.role === 'Admin';

  if (loading) {
    return <LogoLoader message="Loading projects and purchase orders..." />;
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Projects & Purchase Orders</h1>
        <p className="text-gray-600 mt-1">Manage fixed-price projects and client POs independently</p>
      </div>

      <Tabs defaultValue="projects" className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-2">
          <TabsTrigger value="projects">
            <Briefcase className="w-4 h-4 mr-2" />
            Projects (Fixed Lumpsum)
          </TabsTrigger>
          <TabsTrigger value="pos">
            <FileText className="w-4 h-4 mr-2" />
            Purchase Orders (Independent)
          </TabsTrigger>
        </TabsList>

        {/* PROJECTS TAB */}
        <TabsContent value="projects" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Fixed-Price Projects</CardTitle>
                  <CardDescription>
                    Client gives lumpsum amount for entire project. Consultants paid hourly. HR gets fixed commission.
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => { resetProjectForm(); setEditingProject(null); setShowProjectDialog(true); }} 
                  className="bg-utis-forest hover:bg-utis-forest/90 text-white"
                  data-testid="add-project-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Project
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No projects yet. Click "Add Project" to create your first fixed-price project.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Lumpsum Amount</TableHead>
                      <TableHead>HR Commission</TableHead>
                      <TableHead>Consultants</TableHead>
                      <TableHead>Project Status</TableHead>
                      <TableHead>Active Status</TableHead>
                      <TableHead>Toggle</TableHead>
                      {isAdmin && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id} className={!project.is_active ? 'opacity-60 bg-gray-50' : ''}>
                        <TableCell className="font-medium">
                          {project.name}
                          <br />
                          <span className="text-xs text-gray-500">{project.code}</span>
                        </TableCell>
                        <TableCell>{getClientName(project.client_id)}</TableCell>
                        <TableCell className="font-bold text-utis-forest">${project.lumpsum_amount.toLocaleString()}</TableCell>
                        <TableCell>${project.hr_lumpsum_commission.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{project.assigned_consultants?.length || 0} assigned</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-utis-sage/10 text-utis-forest">{project.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {project.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={project.is_active}
                            onCheckedChange={() => toggleProjectStatus(project.id, project.is_active)}
                            data-testid={`toggle-project-${project.id}`}
                          />
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditProject(project)}
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PURCHASE ORDERS TAB */}
        <TabsContent value="pos" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Purchase Orders (POs)</CardTitle>
                  <CardDescription>
                    Client issues PO with lumpsum amount. Consultants bill at their rates. Amount deducted from PO. <strong>No project needed!</strong>
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => { resetPOForm(); setEditingPO(null); setShowPODialog(true); }} 
                  className="bg-utis-forest hover:bg-utis-forest/90 text-white"
                  data-testid="add-po-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add PO
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No POs yet. Click "Add PO" to create your first purchase order.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Lumpsum Amount</TableHead>
                      <TableHead>Consultants</TableHead>
                      <TableHead>PO Status</TableHead>
                      <TableHead>Active Status</TableHead>
                      <TableHead>Toggle</TableHead>
                      {isAdmin && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pos.map((po) => (
                      <TableRow key={po.id} className={!po.is_active ? 'opacity-60 bg-gray-50' : ''}>
                        <TableCell className="font-medium">{po.po_number}</TableCell>
                        <TableCell>{getClientName(po.client_id)}</TableCell>
                        <TableCell>
                          <Badge variant={po.po_type === 'Combined' ? 'default' : 'secondary'}>
                            {po.po_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-bold text-blue-700">${po.lumpsum_amount.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{po.assigned_consultants?.length || 0} assigned</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-utis-sage/10 text-utis-forest">{po.status}</Badge>
                        </TableCell>
                        <TableCell>
                          {po.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={po.is_active}
                            onCheckedChange={() => togglePOStatus(po.id, po.is_active)}
                            data-testid={`toggle-po-${po.id}`}
                          />
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditPO(po)}
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
            <DialogDescription>Fixed-price project with lumpsum amount from client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleProjectSubmit} className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={projectForm.client_id} onValueChange={(val) => setProjectForm({...projectForm, client_id: val})} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_active).map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={projectForm.name} onChange={(e) => setProjectForm({...projectForm, name: e.target.value})} required placeholder="Data Platform Development" />
              </div>
              <div>
                <Label>Project Code *</Label>
                <Input value={projectForm.code} onChange={(e) => setProjectForm({...projectForm, code: e.target.value})} required placeholder="PROJ-2025-001" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lumpsum Amount ($) *</Label>
                <Input type="number" step="0.01" value={projectForm.lumpsum_amount} onChange={(e) => setProjectForm({...projectForm, lumpsum_amount: e.target.value})} required placeholder="100000" />
                <p className="text-xs text-gray-500 mt-1">Total amount client pays for entire project</p>
              </div>
              <div>
                <Label>HR Lumpsum Commission ($)</Label>
                <Input type="number" step="0.01" value={projectForm.hr_lumpsum_commission} onChange={(e) => setProjectForm({...projectForm, hr_lumpsum_commission: e.target.value})} placeholder="10000" />
                <p className="text-xs text-gray-500 mt-1">Fixed commission for HR agents</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={projectForm.start_date} onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})} required />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={projectForm.end_date} onChange={(e) => setProjectForm({...projectForm, end_date: e.target.value})} />
              </div>
            </div>

            <div>
              <Label>Assigned Consultants</Label>
              <Select 
                value={projectForm.assigned_consultants[0] || ''} 
                onValueChange={(val) => {
                  if (!projectForm.assigned_consultants.includes(val)) {
                    setProjectForm({...projectForm, assigned_consultants: [...projectForm.assigned_consultants, val]});
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Add consultants" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.filter(c => c.is_active).map(consultant => (
                    <SelectItem key={consultant.id} value={consultant.id}>{consultant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-2 mt-2">
                {projectForm.assigned_consultants.map(cid => {
                  const consultant = consultants.find(c => c.id === cid);
                  return consultant ? (
                    <Badge key={cid} variant="secondary" className="cursor-pointer" onClick={() => setProjectForm({...projectForm, assigned_consultants: projectForm.assigned_consultants.filter(id => id !== cid)})}>
                      {consultant.name} ×
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={projectForm.notes} onChange={(e) => setProjectForm({...projectForm, notes: e.target.value})} placeholder="Project details..." />
            </div>

            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">
              {editingProject ? 'Update Project' : 'Create Project'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* PO Dialog */}
      <Dialog open={showPODialog} onOpenChange={setShowPODialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPO ? 'Edit Purchase Order' : 'Create New Purchase Order'}</DialogTitle>
            <DialogDescription>Standalone PO - No project required. Client issues PO with lumpsum amount.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePOSubmit} className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={poForm.client_id} onValueChange={(val) => setPoForm({...poForm, client_id: val})} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_active).map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>PO Number *</Label>
                <Input value={poForm.po_number} onChange={(e) => setPoForm({...poForm, po_number: e.target.value})} required placeholder="PO-2025-001" />
              </div>
              <div>
                <Label>PO Type *</Label>
                <Select value={poForm.po_type} onValueChange={(val) => setPoForm({...poForm, po_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Combined">Combined (Multiple Consultants)</SelectItem>
                    <SelectItem value="Individual">Individual (Single Consultant)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lumpsum Amount ($) *</Label>
                <Input type="number" step="0.01" value={poForm.lumpsum_amount} onChange={(e) => setPoForm({...poForm, lumpsum_amount: e.target.value})} required placeholder="50000" />
              </div>
            </div>

            <div>
              <Label>Assign Consultants & Set Billing Rates</Label>
              <Select 
                value="" 
                onValueChange={(val) => addConsultantRate(val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Add consultant with billing rate" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.filter(c => !poForm.assigned_consultants.includes(c.id)).map(consultant => (
                    <SelectItem key={consultant.id} value={consultant.id}>{consultant.name} (Default: ${consultant.default_bill_rate}/hr)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="mt-3 space-y-2">
                {poForm.consultant_rates.map(rate => {
                  const consultant = consultants.find(c => c.id === rate.consultant_id);
                  return consultant ? (
                    <div key={rate.consultant_id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <span className="flex-1 font-medium">{consultant.name}</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={rate.bill_rate}
                        onChange={(e) => updateConsultantRate(rate.consultant_id, e.target.value)}
                        placeholder="Bill rate ($/hr)"
                        className="w-32"
                      />
                      <span className="text-sm text-gray-500">$/hr</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => removeConsultantRate(rate.consultant_id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={poForm.start_date} onChange={(e) => setPoForm({...poForm, start_date: e.target.value})} required />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={poForm.end_date} onChange={(e) => setPoForm({...poForm, end_date: e.target.value})} />
              </div>
            </div>

            <div>
              <Label>Terms</Label>
              <Input value={poForm.terms} onChange={(e) => setPoForm({...poForm, terms: e.target.value})} placeholder="Net 30" />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={poForm.notes} onChange={(e) => setPoForm({...poForm, notes: e.target.value})} placeholder="PO details..." />
            </div>

            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">
              {editingPO ? 'Update PO' : 'Create PO'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
